import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ListAltIcon from "@mui/icons-material/ListAlt";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Logger from "../../services/Logger";
import ConfirmDialog from "../components/ConfirmDialog";
import Item from "../components/Item";
import {
    ADMIN_ADVERTISEMENT_PROMOTION_PATH,
    promotionEditPath,
} from "./advertisementRoutes";
import { DEFAULT_PROMOTION_SUBJECT, forceLinksOpenInNewTab, personalizeGreetingHtml } from "./PromotionEmailHtml";
import PromotionEmailService from "./PromotionEmailService";
import PromotionRecipientsDialog from "./PromotionRecipientsDialog";
import PromotionSendLogDialog from "./PromotionSendLogDialog";
import { renderPromotionTemplate } from "./renderPromotionTemplate";
import {
    applyPromotionToForm,
    buildDuplicatePayload,
    DRAFT_RECIPIENTS_DEFAULT,
    newPromotionCode,
    previewRecipientName,
    previewUnsubscribeUrl,
    PROMOTION_APPS,
    STAGE_OPTIONS,
    STAGE_TEST,
    STATUS_DRAFT,
    STATUS_READY_TO_SENT,
    STATUS_SENT,
    todayNamePrefix,
    uniqueRecipientsByEmail,
    loadStoredPromotionApp,
    persistPromotionApp,
    readStoredPromotionApp,
} from "./promotionEmailUi";

export default function PromotionEditPage() {
    const { code: codeParam } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const isCreate = !codeParam || codeParam === "new";
    const routeCode = !isCreate && codeParam ? decodeURIComponent(codeParam) : "";

    const [code, setCode] = useState(() => newPromotionCode());
    const [name, setName] = useState(() => todayNamePrefix());
    const [subject, setSubject] = useState(DEFAULT_PROMOTION_SUBJECT);
    const [html, setHtml] = useState("");
    const [logo, setLogo] = useState("");
    const [status, setStatus] = useState(STATUS_DRAFT);
    const [app, setApp] = useState(() => loadStoredPromotionApp());
    const [draftRecipients, setDraftRecipients] = useState(DRAFT_RECIPIENTS_DEFAULT);
    const [readyRecipients, setReadyRecipients] = useState([]);
    const [result, setResult] = useState([]);
    const [stage, setStage] = useState(STAGE_TEST);
    const [savedPromotion, setSavedPromotion] = useState(null);
    const [recipientsDialogOpen, setRecipientsDialogOpen] = useState(false);
    const [sendLogOpen, setSendLogOpen] = useState(false);
    const [busyAction, setBusyAction] = useState(null);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [templateBusy, setTemplateBusy] = useState(false);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [loading, setLoading] = useState(!isCreate);
    const templateRequestRef = useRef(0);
    const appRef = useRef(app);
    appRef.current = app;

    const namePrefix = todayNamePrefix();
    const busy = busyAction != null;
    const isSent = status === STATUS_SENT;
    const isSaved = Boolean(savedPromotion?.code || (!isCreate && code));
    const statusOptions = isSent ? [STATUS_SENT] : [STATUS_DRAFT, STATUS_READY_TO_SENT];

    const formSetters = useMemo(
        () => ({
            setName,
            setSubject,
            setCode,
            setStatus,
            setApp,
            setHtml,
            setLogo,
            setDraftRecipients,
            setReadyRecipients,
            setResult,
            setStage,
        }),
        []
    );

    const buildPayload = useCallback(
        (nextStatus) => ({
            code,
            name: name.trim(),
            app,
            subject,
            content: html,
            logo: logo || undefined,
            status: nextStatus || status,
            draftRecipients,
            readyRecipients,
            stage,
        }),
        [code, name, app, subject, html, logo, status, draftRecipients, readyRecipients, stage]
    );

    const previewHtml = useMemo(() => {
        const recipientName = previewRecipientName(status, draftRecipients, readyRecipients, result);
        const unsubscribeUrl = previewUnsubscribeUrl(status, draftRecipients, readyRecipients);
        return forceLinksOpenInNewTab(personalizeGreetingHtml(html, recipientName, unsubscribeUrl));
    }, [html, status, draftRecipients, readyRecipients, result]);

    const previewGreetingName = useMemo(
        () => previewRecipientName(status, draftRecipients, readyRecipients, result),
        [status, draftRecipients, readyRecipients, result]
    );

    const applyTemplateForApp = useCallback(async (nextApp) => {
        const requestId = ++templateRequestRef.current;
        setHtml("");
        setTemplateBusy(true);
        try {
            const data = await renderPromotionTemplate(nextApp);
            if (requestId !== templateRequestRef.current) return;
            if (data?.subject != null) setSubject(data.subject);
            setHtml(typeof data?.htmlContent === "string" ? data.htmlContent : "");
        } catch (err) {
            Logger.error("Promotion template:", err);
            if (requestId === templateRequestRef.current) {
                setError(typeof err === "string" ? err : err?.message || "Failed to load template");
            }
        } finally {
            if (requestId === templateRequestRef.current) {
                setTemplateBusy(false);
            }
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (isCreate) {
                const nextApp = readStoredPromotionApp() || appRef.current;
                setApp(nextApp);
                persistPromotionApp(nextApp);
                setCode(newPromotionCode(nextApp));
                setName(todayNamePrefix());
                setStatus(STATUS_DRAFT);
                setStage(STAGE_TEST);
                setDraftRecipients(DRAFT_RECIPIENTS_DEFAULT);
                setReadyRecipients([]);
                setResult([]);
                setSavedPromotion(null);
                setLoading(false);
                applyTemplateForApp(nextApp);
                return;
            }
            setLoading(true);
            try {
                const saved = await PromotionEmailService.getByCode(routeCode);
                if (cancelled) return;
                if (!saved) {
                    setError("Promotion not found.");
                    return;
                }
                applyPromotionToForm(saved, formSetters);
                setSavedPromotion(saved);
            } catch (err) {
                Logger.error("Load promotion email:", err);
                if (!cancelled) {
                    setError(typeof err === "string" ? err : err?.message || "Failed to load promotion");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isCreate, routeCode, applyTemplateForApp, formSetters]);

    useEffect(() => {
        if (!isCreate) return;
        persistPromotionApp(app);
    }, [app, isCreate]);

    useEffect(() => {
        const msg = location.state?.message;
        if (!msg) return;
        setMessage(msg);
        navigate(location.pathname, { replace: true, state: {} });
    }, [location.state?.message, location.pathname, navigate]);

    const goToSaved = (savedCode) => {
        if (!savedCode) return;
        navigate(promotionEditPath(savedCode), { replace: isCreate });
    };

    const afterPersist = async (response, successMessage) => {
        setMessage(successMessage);
        const savedCode = response?.code || code;
        if (response) {
            applyPromotionToForm(
                {
                    ...response,
                    htmlContent: response.htmlContent ?? html,
                },
                formSetters
            );
            setSavedPromotion(response);
        }
        if (savedCode && savedCode !== routeCode) {
            goToSaved(savedCode);
        }
    };

    const runPersist = async (action, work, successMessage) => {
        setBusyAction(action);
        setMessage("");
        setError("");
        try {
            const response = await work();
            if (response && response.code === "FAILURE") {
                setError(response.message || "Request failed");
                return;
            }
            await afterPersist(response, successMessage);
        } catch (err) {
            setError(err.message || err || "Unknown error");
            Logger.error("Promotion email:", err);
        } finally {
            setBusyAction(null);
        }
    };

    const handleAppChange = (_event, nextApp) => {
        if (!nextApp) return;
        setApp(nextApp);
        persistPromotionApp(nextApp);
        if (isSent) return;
        if (!isSaved) {
            setCode(newPromotionCode(nextApp));
        }
        applyTemplateForApp(nextApp);
    };

    const handleStageChange = async (_event, nextStage) => {
        if (!nextStage) return;
        setStage(nextStage);
        if (!isSaved || status !== STATUS_SENT) return;
        setBusyAction("stage");
        setMessage("");
        setError("");
        try {
            const response = await PromotionEmailService.save({
                ...buildPayload(status),
                stage: nextStage,
            });
            setMessage("Stage saved.");
            if (response) setSavedPromotion(response);
        } catch (err) {
            setError(err.message || err || "Failed to save stage");
            Logger.error("Save promotion stage:", err);
        } finally {
            setBusyAction(null);
        }
    };

    const handleSave = async () => {
        if (isSent) return;
        await runPersist("save", () => PromotionEmailService.save(buildPayload(status)), "Saved.");
    };

    const handleDuplicate = async () => {
        if (!html.trim() || !subject.trim()) return;
        const payload = buildDuplicatePayload({
            name,
            app,
            subject,
            htmlContent: html,
            logo,
            stage,
            readyRecipients,
        });
        await runPersist("duplicate", () => PromotionEmailService.save(payload), "Duplicated as draft.");
    };

    const handleDeleteConfirm = async () => {
        const nextCode = savedPromotion?.code || code;
        if (!nextCode) return;
        setBusyAction("delete");
        setMessage("");
        setError("");
        try {
            await PromotionEmailService.remove(nextCode);
            navigate(ADMIN_ADVERTISEMENT_PROMOTION_PATH, {
                replace: true,
                state: { message: "Promotion and send logs deleted." },
            });
        } catch (err) {
            setError(err.message || err || "Failed to delete promotion");
            Logger.error("Delete promotion email:", err);
            setBusyAction(null);
        }
    };

    const handleRemoveReadyRecipient = (toRemove) => {
        setReadyRecipients((prev) =>
            prev.filter((r) => {
                if (toRemove?.cloudUserId && r?.cloudUserId) {
                    return r.cloudUserId !== toRemove.cloudUserId;
                }
                return (r?.email || "") !== (toRemove?.email || "");
            })
        );
    };

    const handleReadyToSend = async () => {
        if (isSent) return;
        if (!readyRecipients.length) {
            setError("Assign at least one READY_TO_SENT recipient before queuing.");
            setRecipientsDialogOpen(true);
            return;
        }
        await runPersist(
            "ready",
            () => PromotionEmailService.save(buildPayload(STATUS_READY_TO_SENT)),
            `Queued as ${STATUS_READY_TO_SENT} for ${readyRecipients.length} recipient(s).`
        );
    };

    const handleSend = async () => {
        if (isSent) return;
        const targets = status === STATUS_DRAFT ? draftRecipients : readyRecipients;
        if (!targets.length) {
            setError("No recipients assigned for this status.");
            return;
        }
        await runPersist(
            "send",
            () => PromotionEmailService.send(buildPayload(status)),
            `Test send to ${targets.length} recipient(s). Status unchanged.`
        );
    };

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(ADMIN_ADVERTISEMENT_PROMOTION_PATH)}
                sx={{ textTransform: "none", mb: 2 }}
            >
                Promotions
            </Button>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                    <Item>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
                                    {isCreate ? "New promotion" : name || routeCode}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
                                    Edit the Mustache file in this app (src/admin/advertisement/promotion/),
                                    then Load Mustache file so the preview uses that file. Save stores the HTML
                                    in the database. Send now / the scheduler send that saved HTML. The backend
                                    only fills first name and unsubscribe per recipient.
                                </Typography>
                                {loading ? (
                                    <CircularProgress size={28} />
                                ) : (
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 720 }}>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            App
                                        </Typography>
                                        <ToggleButtonGroup
                                            value={app}
                                            exclusive
                                            onChange={handleAppChange}
                                            size="small"
                                            disabled={isSent}
                                            sx={{ flexWrap: "wrap" }}
                                        >
                                            {PROMOTION_APPS.map((option) => (
                                                <ToggleButton
                                                    key={option.id}
                                                    value={option.id}
                                                    disabled={isSent}
                                                    sx={{ textTransform: "none", minHeight: 40 }}
                                                >
                                                    {option.label}
                                                </ToggleButton>
                                            ))}
                                        </ToggleButtonGroup>
                                    </Box>
                                    <TextField
                                        label="Name"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder={namePrefix}
                                        helperText={`Management label only (not shown in the email). Starts with today’s date ${namePrefix} (YYMMDD-) so sends sort clearly in the log.`}
                                        fullWidth
                                        required
                                        disabled={isSent}
                                    />
                                    <FormControl fullWidth disabled={isSent}>
                                        <InputLabel id="promotion-status-label">Status</InputLabel>
                                        <Select
                                            labelId="promotion-status-label"
                                            label="Status"
                                            value={status}
                                            onChange={(event) => setStatus(event.target.value)}
                                            disabled={isSent}
                                        >
                                            {statusOptions.map((value) => (
                                                <MenuItem key={value} value={value}>
                                                    {value}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            Stage
                                        </Typography>
                                        <ToggleButtonGroup
                                            value={stage}
                                            exclusive
                                            onChange={handleStageChange}
                                            size="small"
                                            disabled={busy}
                                        >
                                            {STAGE_OPTIONS.map((value) => (
                                                <ToggleButton
                                                    key={value}
                                                    value={value}
                                                    sx={{ textTransform: "none", minHeight: 40 }}
                                                >
                                                    {value}
                                                </ToggleButton>
                                            ))}
                                        </ToggleButtonGroup>
                                    </Box>
                                    {isSent ? (
                                        <Typography variant="body2" color="text.secondary">
                                            SENT promotions cannot be edited or requeued. Duplicate to create a new draft.
                                        </Typography>
                                    ) : null}
                                    <Box
                                        sx={{
                                            border: 1,
                                            borderColor: "divider",
                                            borderRadius: 1,
                                            p: 2,
                                        }}
                                    >
                                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                            Recipients
                                        </Typography>
                                        {status === STATUS_DRAFT ? (
                                            <>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                    DRAFT / test send uses the fixed inbox:
                                                </Typography>
                                                <Chip
                                                    label="Youngcheol <syc0701@gmail.com>"
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            </>
                                        ) : status === STATUS_READY_TO_SENT ? (
                                            <>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                    READY_TO_SENT recipients ({readyRecipients.length})
                                                </Typography>
                                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1.5 }}>
                                                    {readyRecipients.length === 0 ? (
                                                        <Typography variant="body2" color="text.secondary">
                                                            None assigned yet.
                                                        </Typography>
                                                    ) : (
                                                        readyRecipients.map((r) => (
                                                            <Chip
                                                                key={r.cloudUserId || r.email}
                                                                size="small"
                                                                label={`${r.name || r.email}${
                                                                    r.emailOptedOut
                                                                        ? " · email unsubscribed"
                                                                        : r.pushNotificationsEnabled === false
                                                                          ? " · push off"
                                                                          : ""
                                                                }`}
                                                                variant="outlined"
                                                                onDelete={() => handleRemoveReadyRecipient(r)}
                                                            />
                                                        ))
                                                    )}
                                                </Box>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() => setRecipientsDialogOpen(true)}
                                                    sx={{ textTransform: "none" }}
                                                >
                                                    Assign recipients
                                                </Button>
                                            </>
                                        ) : (
                                            <>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                    Send result ({result.length})
                                                </Typography>
                                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                                                    {result.length === 0 ? (
                                                        <Typography variant="body2" color="text.secondary">
                                                            No sends recorded yet.
                                                        </Typography>
                                                    ) : (
                                                        result.map((r, idx) => (
                                                            <Chip
                                                                key={`${r.email}-${r.date || idx}`}
                                                                size="small"
                                                                color="success"
                                                                variant="outlined"
                                                                label={`${r.name || r.email}${
                                                                    r.date
                                                                        ? ` · ${dayjs(r.date).format("YYYY-MM-DD HH:mm")}`
                                                                        : ""
                                                                }`}
                                                            />
                                                        ))
                                                    )}
                                                </Box>
                                            </>
                                        )}
                                    </Box>
                                    <TextField
                                        label="Subject"
                                        value={subject}
                                        onChange={(e) => setSubject(e.target.value)}
                                        fullWidth
                                        disabled={isSent}
                                    />
                                    {message ? (
                                        <Alert severity="success">{message}</Alert>
                                    ) : null}
                                    {error ? (
                                        <Alert severity="error">{error}</Alert>
                                    ) : null}
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                                        {isSent ? (
                                            <>
                                                <Button
                                                    variant="contained"
                                                    startIcon={
                                                        busyAction === "duplicate" ? (
                                                            <CircularProgress size={18} color="inherit" />
                                                        ) : (
                                                            <ContentCopyIcon />
                                                        )
                                                    }
                                                    onClick={handleDuplicate}
                                                    disabled={busy || !html.trim() || !subject.trim()}
                                                    sx={{ textTransform: "none" }}
                                                >
                                                    Duplicate
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<ListAltIcon />}
                                                    onClick={() => setSendLogOpen(true)}
                                                    disabled={!savedPromotion}
                                                    sx={{ textTransform: "none" }}
                                                >
                                                    Log
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    onClick={() => setConfirmDeleteOpen(true)}
                                                    disabled={busy || !isSaved}
                                                    sx={{ textTransform: "none" }}
                                                >
                                                    Delete
                                                </Button>
                                            </>
                                        ) : (
                                            <>
                                                <Button
                                                    variant="outlined"
                                                    onClick={handleSave}
                                                    disabled={busy || !subject.trim() || !name.trim() || !html.trim()}
                                                    sx={{ textTransform: "none" }}
                                                >
                                                    {busyAction === "save" ? (
                                                        <>
                                                            <CircularProgress size={20} sx={{ mr: 1 }} />
                                                            Saving…
                                                        </>
                                                    ) : (
                                                        "Save"
                                                    )}
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    onClick={handleSend}
                                                    disabled={
                                                        busy
                                                        || !subject.trim()
                                                        || !name.trim()
                                                        || !html.trim()
                                                        || (status === STATUS_READY_TO_SENT && readyRecipients.length === 0)
                                                    }
                                                    sx={{ textTransform: "none" }}
                                                >
                                                    {busyAction === "send" ? (
                                                        <>
                                                            <CircularProgress size={20} sx={{ mr: 1 }} />
                                                            Sending…
                                                        </>
                                                    ) : (
                                                        "Send now"
                                                    )}
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    color="warning"
                                                    onClick={handleReadyToSend}
                                                    disabled={busy || !subject.trim() || !name.trim() || !html.trim()}
                                                    sx={{ textTransform: "none", ml: "auto" }}
                                                >
                                                    {busyAction === "ready" ? (
                                                        <>
                                                            <CircularProgress size={20} sx={{ mr: 1 }} />
                                                            Queuing…
                                                        </>
                                                    ) : (
                                                        "Ready to send"
                                                    )}
                                                </Button>
                                                {isSaved ? (
                                                    <>
                                                        <Button
                                                            variant="outlined"
                                                            startIcon={<ContentCopyIcon />}
                                                            onClick={handleDuplicate}
                                                            disabled={busy || !html.trim() || !subject.trim()}
                                                            sx={{ textTransform: "none" }}
                                                        >
                                                            Duplicate
                                                        </Button>
                                                        <Button
                                                            variant="outlined"
                                                            color="error"
                                                            onClick={() => setConfirmDeleteOpen(true)}
                                                            disabled={busy}
                                                            sx={{ textTransform: "none" }}
                                                        >
                                                            Delete
                                                        </Button>
                                                    </>
                                                ) : null}
                                            </>
                                        )}
                                    </Box>
                                    <Box
                                        sx={{
                                            border: "2px solid #3BA666",
                                            borderRadius: 1,
                                            p: 2,
                                            bgcolor: "background.paper",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                gap: 1,
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            <Typography variant="caption" color="text.secondary">
                                                Preview
                                                {html.trim()
                                                    ? ` · greeting as Hi ${previewGreetingName},`
                                                    : ""}
                                            </Typography>
                                            <Button
                                                size="small"
                                                variant="text"
                                                onClick={() => applyTemplateForApp(appRef.current)}
                                                disabled={templateBusy || isSent}
                                                sx={{ textTransform: "none" }}
                                            >
                                                {templateBusy ? "Loading Mustache file…" : "Load Mustache file"}
                                            </Button>
                                        </Box>
                                        {html.trim() ? (
                                            <Box
                                                sx={{
                                                    mt: 1,
                                                    "& a": { color: "primary.main" },
                                                    "& a.promo-guess-card": {
                                                        color: "#ffffff",
                                                        textDecoration: "none",
                                                    },
                                                    "& a.promo-guess-card p": { color: "#ffffff" },
                                                    "& img.promo-logo": { maxWidth: 64, height: "auto" },
                                                    "& img.promo-company-logo": {
                                                        maxWidth: 32,
                                                        height: "auto",
                                                        display: "block",
                                                    },
                                                    "& img.promo-puzzle": {
                                                        maxWidth: 120,
                                                        width: 120,
                                                        height: "auto",
                                                        borderRadius: 1,
                                                    },
                                                    "& table[role='presentation'] td": {
                                                        verticalAlign: "top",
                                                    },
                                                }}
                                                dangerouslySetInnerHTML={{ __html: previewHtml }}
                                                onClick={(event) => {
                                                    const anchor = event.target.closest("a");
                                                    if (!anchor) return;
                                                    const href = anchor.getAttribute("href");
                                                    if (!href || href.startsWith("#")) return;
                                                    event.preventDefault();
                                                    window.open(href, "_blank", "noopener,noreferrer");
                                                }}
                                            />
                                        ) : (
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                No saved email body yet.
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Item>
                </Grid>
            </Grid>
            <PromotionRecipientsDialog
                open={recipientsDialogOpen}
                onClose={() => setRecipientsDialogOpen(false)}
                app={app}
                selectedRecipients={readyRecipients}
                onSave={(next) => {
                    setReadyRecipients(uniqueRecipientsByEmail(next));
                    setStatus(STATUS_READY_TO_SENT);
                }}
            />
            <PromotionSendLogDialog
                open={sendLogOpen}
                onClose={() => setSendLogOpen(false)}
                promotion={savedPromotion}
            />
            <ConfirmDialog
                open={confirmDeleteOpen}
                setOpen={setConfirmDeleteOpen}
                title="Delete promotion"
                targetName={name || code || ""}
                targetId={savedPromotion?.code || code}
                message="Delete this promotion and its send logs? The mailing list is not changed."
                okText="Delete"
                cancelText="Cancel"
                callback={handleDeleteConfirm}
            />
        </Box>
    );
}
